import React, { useEffect, useId, useMemo, useState } from 'react';
import apiService from '../../services/api';
import type {
  EmailProviderPreset,
  McpConfigureBuiltinPreview,
  McpConfigureBuiltinRequest,
  SlackSetupPreset,
} from '../../types';

/** The Email built-in server id. */
const EMAIL_SERVER_ID = -2;
/** The Slack built-in server id (AC-1807, epic AC-1803). */
const SLACK_SERVER_ID = -3;

/** Per-card lifecycle, mirroring McpProposalCard but for the built-in setup gate. */
export type McpConfigureBuiltinState =
  | 'pending'
  | 'configuring'
  | 'configured'
  | 'declined'
  | 'superseded';

interface McpConfigureBuiltinCardProps {
  preview: McpConfigureBuiltinPreview;
  state: McpConfigureBuiltinState;
  /** Connect: carries the built-in id + a SaveCredentialRequest-shaped payload. */
  onConfirm: (req: McpConfigureBuiltinRequest) => void;
  onDecline: () => void;
}

/**
 * McpConfigureBuiltinCard (AC-1746, defect AC-1743, epic AC-1732). The in-chat
 * setup gate for a built-in tool the user has NOT configured yet. When the
 * assistant resolves a capability need to a built-in whose credentials are
 * missing (e.g. "read my emails" -> the Email built-in), it surfaces this card
 * inline so the user can connect the built-in WITHOUT leaving chat, then re-issue
 * the original request (now satisfiable via Tier-1 reuse).
 *
 * Built as a sibling of McpProposalCard (same accessible role="group" region,
 * text-not-color status, >=44px tap targets). For Email (builtin_server_id === -2)
 * it renders a compact version of the existing email setup form: a provider
 * select sourced from GET /api/v1/mcp/email-providers, an email field, and an
 * app-password field (type="password"). Host/port are auto-filled from the chosen
 * preset and carried on the confirm payload — never typed by the user. Form state
 * is local/ephemeral; the confirm is replayed server-side by
 * BuiltinCredentialService.save_credentials (the credential write path is reused
 * verbatim, never forked).
 *
 * For Slack (builtin_server_id === -3, AC-1807 / epic AC-1803) it renders a
 * single "Bot User OAuth Token" secret field (type="password") plus preset-driven
 * guided steps loaded from GET /api/v1/mcp/slack-setup. Connect stays disabled
 * until a non-empty trimmed token starting with the preset's `token_prefix`
 * ("xoxb-") is entered, and on Connect it emits a Slack-shaped payload
 * { provider: "slack", bot_token } — NOT the email host/port credentials. The two
 * branches are mutually exclusive on builtin_server_id; the Email branch is
 * unchanged.
 *
 * State drives affordances:
 *   - 'configuring' : Connect/Decline disabled + aria-busy on the container.
 *   - 'configured'  : a connected indicator replaces the actions.
 *   - 'declined'    : a dimmed "Declined" note.
 *   - 'superseded'  : Connect disabled (a newer turn replaced this card).
 */
const McpConfigureBuiltinCard: React.FC<McpConfigureBuiltinCardProps> = ({
  preview,
  state,
  onConfirm,
  onDecline,
}) => {
  const headingId = useId();
  const messageId = useId();
  const providerId = useId();
  const emailId = useId();
  const passwordId = useId();
  // AC-1759: ids wired into the password input's aria-describedby so a
  // screen-reader user focusing the secret field hears the app-password warning
  // alongside the existing encryption-reassurance text.
  const warningId = useId();
  const reassuranceId = useId();
  const helpRegionId = useId();
  // AC-1807: ids for the Slack branch's token field + its reassurance/help region.
  const slackTokenId = useId();
  const slackReassuranceId = useId();
  const slackHelpRegionId = useId();

  const isEmail = preview.builtin_server_id === EMAIL_SERVER_ID;
  const isSlack = preview.builtin_server_id === SLACK_SERVER_ID;

  const [providers, setProviders] = useState<EmailProviderPreset[]>([]);
  const [providerId_, setProviderId] = useState('');
  const [email, setEmail] = useState('');
  // Never pre-filled: the secret starts empty and lives only in local state +
  // the confirm request (never logged).
  const [appPassword, setAppPassword] = useState('');
  // AC-1807: Slack guided-setup preset + the Bot User OAuth Token the user pastes.
  // The token starts empty (never pre-filled) and lives only in local state + the
  // confirm request.
  const [slackSetup, setSlackSetup] = useState<SlackSetupPreset | null>(null);
  const [slackToken, setSlackToken] = useState('');
  // AC-1759: the "How do I get an app password?" disclosure is collapsed by
  // default so the narrow chat column stays scannable and the Connect button
  // stays above the fold; help is one tap away on demand.
  const [helpOpen, setHelpOpen] = useState(false);

  const isConfiguring = state === 'configuring';
  const isConfigured = state === 'configured';
  const isDeclined = state === 'declined';
  const isSuperseded = state === 'superseded';
  const isLocked = isConfiguring || isConfigured || isDeclined;

  // Load the email provider presets so the select can offer Gmail/Yahoo/etc. The
  // presets carry the host/port we attach to the confirm payload, so the user
  // never has to type them.
  useEffect(() => {
    if (!isEmail) return;
    let cancelled = false;
    apiService
      .getEmailProviders()
      .then((data) => {
        if (!cancelled) setProviders(data);
      })
      .catch(() => {
        // Silently degrade: the select renders empty and the user can retry.
      });
    return () => {
      cancelled = true;
    };
  }, [isEmail]);

  const selectedProvider = useMemo(
    () => providers.find((p) => p.id === providerId_) ?? null,
    [providers, providerId_],
  );

  // AC-1807: load the Slack guided-setup preset (token label, help steps/url/why,
  // the xoxb- prefix used to gate Connect) once on mount when this card is the
  // Slack built-in. Mirrors the email-providers fetch above.
  useEffect(() => {
    if (!isSlack) return;
    let cancelled = false;
    apiService
      .getSlackSetup()
      .then((data) => {
        if (!cancelled) setSlackSetup(data);
      })
      .catch(() => {
        // Silently degrade: the help disclosure stays empty and the user can retry.
      });
    return () => {
      cancelled = true;
    };
  }, [isSlack]);

  // AC-1807: client-side Connect gate for Slack. The value must be a non-empty
  // trimmed token starting with the preset's prefix ("xoxb-"). The prefix check
  // is CASE-INSENSITIVE (AC-1811) so a pasted "XOXB-..." isn't wrongly rejected;
  // the backend re-validates (also case-insensitively) and Slack's auth decides
  // real validity. We fall back to the literal "xoxb-" when the preset hasn't
  // loaded yet so the gate is sane.
  const slackTokenPrefix = slackSetup?.token_prefix || 'xoxb-';
  const slackTokenValid = slackToken
    .trim()
    .toLowerCase()
    .startsWith(slackTokenPrefix.toLowerCase());

  const handleConnect = () => {
    // AC-1807: the Slack branch emits a Slack-shaped payload (provider + bot_token),
    // NOT the email host/port credentials. Connect is gated on slackTokenValid, so
    // the value is a non-empty xoxb- token by the time this fires.
    if (isSlack) {
      onConfirm({
        builtin_server_id: preview.builtin_server_id,
        credentials: {
          provider: 'slack',
          bot_token: slackToken.trim(),
        },
      });
      return;
    }

    const credentials: Record<string, any> = {
      provider: providerId_,
      email,
      app_password: appPassword,
    };
    // Host/port come from the chosen preset, never from a user-typed field.
    if (selectedProvider) {
      credentials.imap_host = selectedProvider.imap_host;
      credentials.imap_port = selectedProvider.imap_port;
      credentials.smtp_host = selectedProvider.smtp_host;
      credentials.smtp_port = selectedProvider.smtp_port;
    }
    onConfirm({
      builtin_server_id: preview.builtin_server_id,
      credentials,
    });
  };

  // The Connect button is additionally gated for Slack: it stays disabled until a
  // valid xoxb- token is entered (the email branch has no such field-level gate).
  const connectDisabled =
    isConfiguring || isSuperseded || (isSlack && !slackTokenValid);

  return (
    <div
      role="group"
      aria-labelledby={headingId}
      aria-describedby={messageId}
      aria-live="polite"
      aria-busy={isConfiguring ? 'true' : undefined}
      className={`mt-2 pt-2 border-t border-gray-200 dark:border-gray-700 flex flex-col gap-3 focus:outline-none ${
        isDeclined ? 'opacity-50' : ''
      }`}
    >
      <div>
        {/* Generic heading (no built-in name) — the built-in being configured is
            named in the id-free message below, mirroring McpProposalCard's
            generic "Add a remote integration?" heading. */}
        <h4
          id={headingId}
          className="text-sm font-semibold text-gray-900 dark:text-warm-white"
        >
          Set up this tool
        </h4>
        {/* id-free "why this matched" / setup explanation (names the built-in).
            Hidden once configured so the success indicator is the sole status
            text (the setup message itself mentions the account being
            "connected"). */}
        {!isConfigured && (
          <p
            id={messageId}
            className="text-xs text-gray-600 dark:text-gray-400 mt-0.5"
          >
            {preview.message}
          </p>
        )}
      </div>

      {/* Email setup form (builtin_server_id === -2). Inputs carry accessible
          names via aria-label so a single visible "Email" text node (the message)
          remains, while still exposing labelled controls to assistive tech. */}
      {isEmail && (
        <div className="flex flex-col gap-3">
          {/* The provider select renders once the presets resolve so its options
              (Gmail/Yahoo/...) — which carry the host/port we attach to the
              confirm payload — are always present when a provider is chosen. */}
          {providers.length > 0 && (
            <div className="flex flex-col gap-1">
              <label
                htmlFor={providerId}
                className="text-xs font-medium text-gray-700 dark:text-gray-300"
              >
                Provider
              </label>
              <select
                id={providerId}
                value={providerId_}
                onChange={(e) => setProviderId(e.target.value)}
                disabled={isLocked || isSuperseded}
                className="min-h-[44px] w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-warm-white focus:outline-none focus:ring-2 focus:ring-primary-400 disabled:opacity-50"
              >
                <option value="">Select a provider</option>
                {providers.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="flex flex-col gap-1">
            <input
              id={emailId}
              type="email"
              aria-label="Email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isLocked || isSuperseded}
              autoComplete="off"
              placeholder="you@example.com"
              className="min-h-[44px] w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-warm-white focus:outline-none focus:ring-2 focus:ring-primary-400 disabled:opacity-50"
            />
          </div>

          {/* AC-1759: preventive guidance shown ABOVE the password field (and so
              ABOVE the input it describes) — non-error, neutral info styling. The
              regular account password silently fails IMAP/SMTP auth today, so this
              is shown before the user types. Conveyed in text (not color) for
              WCAG; contrast meets 4.5:1 via gray-700 / dark:gray-300. */}
          <p
            id={warningId}
            className="text-xs text-gray-700 dark:text-gray-300 border-l-2 border-gray-300 dark:border-gray-600 pl-2"
          >
            <span className="font-semibold">
              Use an app password — not your usual email password.
            </span>{' '}
            Your regular password won't connect here. Most providers make you
            create a one-time app password for apps like this.
          </p>

          <div className="flex flex-col gap-1">
            <input
              id={passwordId}
              type="password"
              aria-label="App password"
              aria-describedby={`${warningId} ${reassuranceId}`}
              value={appPassword}
              onChange={(e) => setAppPassword(e.target.value)}
              disabled={isLocked || isSuperseded}
              autoComplete="off"
              spellCheck={false}
              placeholder="App password — not your account password"
              className="min-h-[44px] w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-warm-white focus:outline-none focus:ring-2 focus:ring-primary-400 disabled:opacity-50"
            />
            <p
              id={reassuranceId}
              className="text-xs text-gray-500 dark:text-gray-400"
            >
              Sent only when you connect, then stored encrypted. Never logged.
            </p>
          </div>

          {/* AC-1759: "How do I get an app password?" disclosure. Real <button>
              (keyboard-operable, Enter/Space native) carrying aria-expanded +
              aria-controls; the revealed region is conditionally rendered (not
              CSS-hidden) so AT doesn't announce collapsed steps. Guidance is
              entirely preset-driven (why / help_steps / help_url) — nothing is
              hardcoded in the card. */}
          <div className="flex flex-col gap-1">
            <button
              type="button"
              onClick={() => setHelpOpen((open) => !open)}
              aria-expanded={helpOpen}
              aria-controls={helpRegionId}
              className="flex items-center gap-1 min-h-[44px] self-start text-left text-xs font-medium text-primary-600 dark:text-primary-400 hover:underline focus:outline-none focus:ring-2 focus:ring-primary-400 rounded"
            >
              <span
                aria-hidden="true"
                className={`inline-block transition-transform ${
                  helpOpen ? 'rotate-90' : ''
                }`}
              >
                ▸
              </span>
              How do I get an app password?
            </button>

            {helpOpen && (
              <div
                id={helpRegionId}
                className="flex flex-col gap-2 text-xs text-gray-700 dark:text-gray-300 pl-2"
              >
                {selectedProvider ? (
                  <>
                    <p>{selectedProvider.why}</p>
                    {selectedProvider.help_steps.length > 0 && (
                      <ol className="list-decimal pl-4 flex flex-col gap-1">
                        {selectedProvider.help_steps.map((step, idx) => (
                          <li key={idx}>{step}</li>
                        ))}
                      </ol>
                    )}
                    <a
                      href={selectedProvider.help_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 min-h-[44px] font-medium text-primary-600 dark:text-primary-400 underline focus:outline-none focus:ring-2 focus:ring-primary-400 rounded"
                    >
                      Open {selectedProvider.name}'s app password page
                      <span aria-hidden="true">↗</span>
                      <span className="sr-only">(opens in a new tab)</span>
                    </a>
                  </>
                ) : (
                  <p>
                    Pick your provider above, then we'll show the exact steps and
                    link.
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* AC-1807: Slack setup form (builtin_server_id === -3). A single
          type="password" Bot User OAuth Token field plus preset-driven guided
          steps. All copy (token label, help steps, help url, why note) comes from
          GET /api/v1/mcp/slack-setup — nothing is hardcoded in the card. Connect is
          gated on a non-empty xoxb- value (slackTokenValid). */}
      {isSlack && (
        <div className="flex flex-col gap-3">
          {/* Preventive guidance shown ABOVE the token field: a normal Slack
              login/password will not work; a Bot User OAuth Token is required.
              Conveyed in text (not color) for WCAG. Preset-driven `why` note. */}
          {slackSetup?.why && (
            <p className="text-xs text-gray-700 dark:text-gray-300 border-l-2 border-gray-300 dark:border-gray-600 pl-2">
              {slackSetup.why}
            </p>
          )}

          <div className="flex flex-col gap-1">
            <label
              htmlFor={slackTokenId}
              className="text-xs font-medium text-gray-700 dark:text-gray-300"
            >
              {slackSetup?.token_label || 'Bot User OAuth Token'}
            </label>
            <input
              id={slackTokenId}
              type="password"
              aria-label={slackSetup?.token_label || 'Bot User OAuth Token'}
              aria-describedby={slackReassuranceId}
              value={slackToken}
              onChange={(e) => setSlackToken(e.target.value)}
              disabled={isLocked || isSuperseded}
              autoComplete="off"
              spellCheck={false}
              placeholder={`${slackTokenPrefix}...`}
              className="min-h-[44px] w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-warm-white focus:outline-none focus:ring-2 focus:ring-primary-400 disabled:opacity-50"
            />
            <p
              id={slackReassuranceId}
              className="text-xs text-gray-500 dark:text-gray-400"
            >
              Sent only when you connect, then stored encrypted. Never logged.
            </p>
          </div>

          {/* "How do I get a bot token?" disclosure. Real <button>
              (keyboard-operable, Enter/Space native) carrying aria-expanded +
              aria-controls; the revealed region is conditionally rendered (not
              CSS-hidden). Guidance is entirely preset-driven (why / help_steps /
              help_url) — nothing is hardcoded in the card. */}
          <div className="flex flex-col gap-1">
            <button
              type="button"
              onClick={() => setHelpOpen((open) => !open)}
              aria-expanded={helpOpen}
              aria-controls={slackHelpRegionId}
              className="flex items-center gap-1 min-h-[44px] self-start text-left text-xs font-medium text-primary-600 dark:text-primary-400 hover:underline focus:outline-none focus:ring-2 focus:ring-primary-400 rounded"
            >
              <span
                aria-hidden="true"
                className={`inline-block transition-transform ${
                  helpOpen ? 'rotate-90' : ''
                }`}
              >
                ▸
              </span>
              How do I get a bot token?
            </button>

            {helpOpen && (
              <div
                id={slackHelpRegionId}
                className="flex flex-col gap-2 text-xs text-gray-700 dark:text-gray-300 pl-2"
              >
                {slackSetup ? (
                  <>
                    {slackSetup.help_steps.length > 0 && (
                      <ol className="list-decimal pl-4 flex flex-col gap-1">
                        {slackSetup.help_steps.map((step, idx) => (
                          <li key={idx}>{step}</li>
                        ))}
                      </ol>
                    )}
                    <a
                      href={slackSetup.help_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 min-h-[44px] font-medium text-primary-600 dark:text-primary-400 underline focus:outline-none focus:ring-2 focus:ring-primary-400 rounded"
                    >
                      Open Slack app management
                      <span aria-hidden="true">↗</span>
                      <span className="sr-only">(opens in a new tab)</span>
                    </a>
                  </>
                ) : (
                  <p>Loading the Slack setup steps…</p>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Actions / status (text, not color-only). */}
      {isConfigured ? (
        <div className="text-xs font-medium text-green-700 dark:text-green-400">
          {preview.server_name} connected
        </div>
      ) : isDeclined ? (
        <div className="text-xs text-gray-500 dark:text-gray-400">
          Declined — nothing was saved
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          <button
            type="button"
            onClick={handleConnect}
            disabled={connectDisabled}
            className="w-full min-h-[44px] text-sm font-medium bg-blue-500 text-white px-3 py-2 rounded-md hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Connect
          </button>
          <button
            type="button"
            onClick={onDecline}
            disabled={isConfiguring}
            className="w-full min-h-[44px] text-sm font-medium border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 px-3 py-2 rounded-md hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Decline
          </button>
        </div>
      )}
    </div>
  );
};

export default McpConfigureBuiltinCard;
